import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  Fab,
  Drawer,
  useTheme,
  useMediaQuery,
  Rating,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  keyframes
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Info as InfoIcon,
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  Kitchen as KitchenIcon,
  Pets as PetsIcon,
  SmokeFree as SmokeFreeIcon,
  AcUnit as AcIcon,
  Pool as PoolIcon,
  FitnessCenter as GymIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategoryName } from '../utils/categoryTranslation';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useCurrency } from '../context/CurrencyContext';
import { useFavorites } from '../context/FavoritesContext';
import api from '../services/api';
import ReadOnlyMap from '../components/common/ReadOnlyMap';
import ReservationSidebar from '../components/booking/ReservationSidebar';
import PricingGrid from '../components/booking/PricingGrid';
import BookingSettingsSummary from '../components/booking/BookingSettingsSummary';
import UserReputationModal from '../components/reputation/UserReputationModal';
import ListingReviewsList from '../components/reviews/ListingReviewsList';
import RideShareMap from '../components/listing/RideShareMap';
import ListingDetailsView from '../components/listing/ListingDetailsView';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
const getUploadedImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${UPLOADS_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
};

// Animation de pulsation pour le FAB
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(0,0,0,0.2), 0 0 0 0 rgba(25, 118, 210, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 25px rgba(0,0,0,0.3), 0 0 0 10px rgba(25, 118, 210, 0.3);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(0,0,0,0.2), 0 0 0 0 rgba(25, 118, 210, 0.7);
  }
`;

const ListingViewPage = () => {
  const { t } = useTranslation(['common', 'booking']);
  const getCategoryName = useCategoryName();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { formatPrice } = useCurrency();
  const { isFavorite, isPending, toggleFavorite } = useFavorites();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // États
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [ownerStats, setOwnerStats] = useState(null);
  const [ownerStatsLoading, setOwnerStatsLoading] = useState(false);
  const [reputationModalOpen, setReputationModalOpen] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState({
    details: true,
    pricing: true,
    amenities: false,
    address: false,
    location: false,
    owner: true,
    listingDetails: true
  });

  // Gestion de l'expansion des accordéons
  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  // Navigation des images
  const goToNextImage = () => {
    if (listing?.images && listing.images.length > 1) {
      setSelectedImage(prev => prev === listing.images.length - 1 ? 0 : prev + 1);
    }
  };

  const goToPreviousImage = () => {
    if (listing?.images && listing.images.length > 1) {
      setSelectedImage(prev => prev === 0 ? listing.images.length - 1 : prev - 1);
    }
  };

  // Navigation clavier pour les images
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (listing?.images && listing.images.length > 1) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goToPreviousImage();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goToNextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [listing?.images, selectedImage]);

  // Charger les données de l'annonce
  const fetchListing = async () => {
    try {
      setLoading(true);
      console.log('[ListingViewPage] Fetching listing details...', { id });

      // Toujours ajouter un timestamp pour contourner le cache
      const timestamp = new Date().getTime();
      const url = `/listings/${id}?_t=${timestamp}`;
      
      console.log('[ListingViewPage] Making API request to:', url);

      const response = await api.get(url);
      const data = response.data;
      
      console.log('[ListingViewPage] Full API response:', data);
      console.log('[ListingViewPage] Listing data:', data.listing);
      console.log('[ListingViewPage] Owner data:', data.listing?.owner);

      if (data.listing) {
        // Si ce listing est le jumeau d'un produit, rediriger vers la page commande produit
        if (data.listing.twinProductId) {
          navigate(`/product/${data.listing.twinProductId}/order`, { replace: true });
          return;
        }

        console.log('[ListingViewPage] Setting listing state with data:', {
          title: data.listing.title,
          price: data.listing.price_per_day,
          description: data.listing.description,
          images: data.listing.images,
          category: data.listing.category,
          owner: data.listing.owner
        });
        setListing(data.listing);
        
        // Charger les statistiques du propriétaire si on a un ID
        if (data.listing.owner?.id) {
          fetchOwnerStats(data.listing.owner.id);
        }
      } else {
        console.error('[ListingViewPage] No listing found in API response');
        throw new Error('No listing data found');
      }
    } catch (error) {
      console.error('[ListingViewPage] Error fetching listing:', error);
      showNotification(t('booking:listingView.notFound'), 'error');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Charger les statistiques du propriétaire
  const fetchOwnerStats = async (ownerId) => {
    try {
      setOwnerStatsLoading(true);
      console.log('[ListingViewPage] Fetching owner stats for:', ownerId);

      // Appel à l'API pour récupérer la réputation du propriétaire
      const response = await api.get(`/users/${ownerId}/reputation`);
      const data = response.data.data;
      
      console.log('[ListingViewPage] Owner reputation loaded:', data);
      
      // Mapper les données de réputation vers le format ownerStats
      setOwnerStats({
        totalListings: data.reputation?.activeListingsCount || 0,
        averageRating: data.reputation?.score || 0,
        totalReviews: data.reputation?.totalReviews || 0,
        responseRate: 0, // Non disponible dans cette API
        memberSince: data.user?.memberSince || null
      });
    } catch (error) {
      console.error('[ListingViewPage] Error fetching owner stats:', error);
      // En cas d'erreur, on peut définir des valeurs par défaut
      setOwnerStats({
        totalListings: 0,
        averageRating: 0,
        totalReviews: 0,
        responseRate: 0,
        memberSince: null
      });
    } finally {
      setOwnerStatsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchListing();
    }
  }, [id]);

  // Booking uniquement si listing actif
  const isListingBookable = listing?.status === 'active';

  // Si le listing n'est pas réservable, on force la fermeture de la sidebar de réservation
  useEffect(() => {
    if (listing && !isListingBookable && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [listing, isListingBookable, sidebarOpen]);

  // Favoris
  const favorited = isFavorite('listing', listing?.id);
  const favoritePending = isPending('listing', listing?.id);

  const handleFavoriteToggle = async () => {
    if (!user) {
      showNotification(t('search.favorite.loginRequired', 'Connectez-vous pour ajouter aux favoris'), 'info');
      return;
    }

    try {
      const result = await toggleFavorite({ type: 'listing', targetId: listing.id });
      if (!result?.ignored) {
        showNotification(
          result?.isFavorite ? t('search.favorite.added', 'Ajouté aux favoris') : t('search.favorite.removed', 'Retiré des favoris'),
          'success'
        );
      }
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Impossible de mettre à jour le favori', 'error');
    }
  };

  // Partager l'annonce
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: listing?.title,
          text: listing?.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log(t('booking:listingView.shareCancelled'));
      }
    } else {
      // Fallback : copier l'URL
      navigator.clipboard.writeText(window.location.href);
      showNotification(t('booking:listingView.shareSuccess'), 'success');
    }
  };

  // Formater le prix avec la devise de l'annonce
  const formatListingPrice = (price) => {
    const numericPrice = Number(price);
    const isValidPrice = !isNaN(numericPrice) && numericPrice > 0;
    const currency = listing?.currency || 'EUR';
    
    if (!isValidPrice) {
      return 'N/A';
    }
    
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(numericPrice);
    } catch (error) {
      return `${numericPrice} ${currency}`;
    }
  };

  // Formater les dates
  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      // Format français : mois/année
      const options = { 
        year: 'numeric', 
        month: 'long'
      };
      
      return date.toLocaleDateString('fr-FR', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Icônes des équipements
  const getAmenityIcon = (amenity) => {
    switch (amenity.toLowerCase()) {
      case 'wifi':
        return <WifiIcon />;
      case 'parking':
        return <ParkingIcon />;
      case 'kitchen':
      case 'cuisine':
        return <KitchenIcon />;
      case 'pets':
      case 'animaux':
        return <PetsIcon />;
      case 'no smoking':
      case 'non-fumeur':
        return <SmokeFreeIcon />;
      case 'air conditioning':
      case 'climatisation':
        return <AcIcon />;
      case 'pool':
      case 'piscine':
        return <PoolIcon />;
      case 'gym':
      case 'salle de sport':
        return <GymIcon />;
      default:
        return <InfoIcon />;
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!listing) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {t('booking:listingView.notFound')}
        </Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/')}
          sx={{
            mr: 2,
            border: '1px solid #6200ee',
            borderColor: '#6200ee',
            color: '#6200ee',
            fontWeight: 'medium',
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              color: 'white',
              borderColor: 'transparent',
              boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('booking:listingView.backToHome')}
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 2, position: 'relative' }}>
      <Container maxWidth="xl">
        {/* Header avec bouton retour */}
        <Box mb={3} display="flex" justifyContent="flex-start" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              border: '1px solid #6200ee',
              borderColor: '#6200ee',
              color: '#6200ee',
              fontWeight: 'medium',
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
              '&:hover': {
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                borderColor: 'transparent',
                boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('booking:listingView.back')}
          </Button>
        </Box>

        {/* Fab flottant pour ouvrir la sidebar - Mi-hauteur avec pulsations */}
        {isListingBookable && !sidebarOpen && (
          <Fab
            color="primary"
            aria-label={t('booking:listingView.openReservation')}
            onClick={() => setSidebarOpen(true)}
            sx={{
              position: 'fixed',
              top: '50%',
              right: 24,
              transform: 'translateY(-50%)',
              zIndex: 1301,
              animation: `${pulseAnimation} 2s ease-in-out infinite`
            }}
          >
            <CalendarIcon />
          </Fab>
        )}

        {/* Bloc info si réservation impossible (listing non actif) */}
        {!isListingBookable && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: theme.palette.warning.dark,
              color: 'white',
              '& .MuiAlert-icon': { color: 'white' }
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white' }}>
              {t('booking:listingView.unavailable.title', 'Réservation indisponible')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('booking:listingView.unavailable.messagePrefix', 'Le statut de cette annonce est')}{' '}
              <strong>{t(`booking:listingView.status.${listing?.status}`, listing?.status || 'inconnu')}</strong>{' '}
              {t('booking:listingView.unavailable.messageSuffix', 'Seules les annonces au statut active peuvent être réservées.')}
            </Typography>
          </Alert>
        )}

        {/* Titre au-dessus du carrousel */}
        <Box
          sx={{
            mb: 2,
            py: 2,
            px: 3,
            borderRadius: 2,
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'}>
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  {listing?.title}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {listing?.categoryData && <Chip label={getCategoryName(listing.categoryData)} size="small" variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} />}
                  {listing?.city && <Chip label={listing.city} size="small" variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} />}
                  {listing?.price_daily && <Chip label={`${formatListingPrice(listing.price_daily)} ${t('booking:pricingGrid.units.day', '/ jour')}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />}
                  {listing?.rating_avg > 0 && <Chip label={`★ ${Number(listing.rating_avg).toFixed(1)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton
                  onClick={handleFavoriteToggle}
                  disabled={favoritePending || !listing?.id}
                  color={favorited ? 'error' : 'default'}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
                >
                  {favorited ? <FavoriteIcon /> : <FavoriteBorderIcon sx={{ color: 'white' }} />}
                </IconButton>
                <IconButton onClick={handleShare} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                  <ShareIcon sx={{ color: 'white' }} />
                </IconButton>
              </Stack>
            </Stack>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {listing?.address || listing?.city || t('booking:listingView.locationPrivacy')}
                </Typography>
              </Box>
              {listing?.owner?.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {listing.owner.name}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Contenu principal */}
        <Grid container spacing={3} justifyContent="center">
          {/* Section principale avec les 3 sections empilées verticalement */}
          <Grid item xs={12} lg={9}>
            {/* Images */}
            <Card sx={{ mb: 3, position: 'relative' }}>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="400"
                  image={getUploadedImageUrl(listing?.images?.[selectedImage]?.url) || '/assets/placeholder-equipment.svg'}
                  alt={listing?.title || 'Image'}
                  sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                />
                
                {/* Flèches de navigation */}
                {listing?.images && listing.images.length > 1 && (
                  <>
                    {/* Flèche gauche */}
                    <IconButton
                      onClick={goToPreviousImage}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        zIndex: 1,
                        width: 48,
                        height: 48
                      }}
                    >
                      <ChevronLeftIcon fontSize="large" />
                    </IconButton>
                    
                    {/* Flèche droite */}
                    <IconButton
                      onClick={goToNextImage}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        zIndex: 1,
                        width: 48,
                        height: 48
                      }}
                    >
                      <ChevronRightIcon fontSize="large" />
                    </IconButton>
                    
                    {/* Indicateur de position */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        right: 16,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        fontSize: '0.875rem',
                        fontWeight: 'medium'
                      }}
                    >
                      {selectedImage + 1} / {listing.images.length}
                    </Box>
                  </>
                )}
              </Box>
              
              {/* Miniatures */}
              {listing?.images && listing.images.length > 1 && (
                <Box sx={{ p: 2, display: 'flex', gap: 1, overflow: 'auto' }}>
                  {listing.images.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: index === selectedImage ? '3px solid #6200ee' : '1px solid #ddd',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        }
                      }}
                      onClick={() => setSelectedImage(index)}
                    >
                      <img
                        src={getUploadedImageUrl(image.url)}
                        alt={`${listing?.title} ${index + 1}`}
                        style={{
                          width: 80,
                          height: 60,
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      {index === selectedImage && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(98, 0, 238, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: '#6200ee',
                              border: '2px solid white'
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Card>

            {/* Description */}
            <Accordion 
              expanded={expandedPanels.details} 
              onChange={handlePanelChange('details')}
              sx={{ 
                mb: 3,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  📝 {t('booking:listingView.sections.description')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {listing?.description || t('booking:listingView.noDescription')}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Détails spécifiques */}
            <ListingDetailsView
              listing={listing}
              expanded={expandedPanels.listingDetails}
              onChange={handlePanelChange('listingDetails')}
            />

            {/* Grille de Prix */}
            <Accordion 
              expanded={expandedPanels.pricing} 
              onChange={handlePanelChange('pricing')}
              sx={{ 
                mb: 3,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)'
                  }
                }}
              >
                <EuroIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  💰 {t('booking:listingView.sections.pricingGrid')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <PricingGrid listing={listing} />
              </AccordionDetails>
            </Accordion>

            {/* Paramètres de réservation */}
            {listing?.booking_mode && (
              <BookingSettingsSummary listing={listing} variant="accordion" defaultExpanded />
            )}

            {/* Équipements */}
            {listing?.amenities && listing.amenities.length > 0 && (
              <Accordion 
                expanded={expandedPanels.amenities} 
                onChange={handlePanelChange('amenities')}
                sx={{ 
                  mb: 3,
                  boxShadow: 2,
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                    color: 'white',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center'
                    },
                    '&:hover': {
                      background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    ✨ {t('booking:listingView.sections.amenities')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {listing.amenities.map((amenity, index) => (
                      <Grid item xs={6} sm={4} key={index}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getAmenityIcon(amenity)}
                          <Typography variant="body2">
                            {amenity}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

{/* Horaires début / fin — pour tous les listings en mode date_range */}
            {listing?.booking_mode === 'date_range' && (listing?.booking_settings?.checkin_time || listing?.booking_settings?.checkout_time) && (
              <Accordion
                sx={{
                  mb: 3,
                  boxShadow: 2,
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                    color: 'white',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center'
                    },
                    '&:hover': {
                      background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                    }
                  }}
                >
                  <ScheduleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {listing.offer_type === 'property'
                      ? t('listing:property.section')
                      : t('listing:dateRange.section')
                    }
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          {listing.offer_type === 'property'
                            ? t('listing:property.checkinLabel')
                            : t('listing:dateRange.startLabel')
                          }
                        </Typography>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          {listing.booking_settings?.checkin_time || (listing.offer_type === 'property' ? '14:00' : '09:00')}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          {listing.offer_type === 'property'
                            ? t('listing:property.checkoutLabel')
                            : t('listing:dateRange.endLabel')
                          }
                        </Typography>
                        <Typography variant="h5" color="secondary" fontWeight="bold">
                          {listing.booking_settings?.checkout_time || (listing.offer_type === 'property' ? '12:00' : '18:00')}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Adresse avec carte interactive */}
            <Accordion 
              expanded={expandedPanels.address} 
              onChange={handlePanelChange('address')}
              sx={{ 
                mb: 3,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <LocationIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {t('booking:listingView.sections.location')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                {/* Informations d'adresse */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                    {listing?.address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {listing?.city}{listing?.postalCode && `, ${listing.postalCode}`}
                  </Typography>
                  {listing?.region && (
                    <Typography variant="body2" color="text.secondary">
                      {listing.region}{listing?.country && `, ${listing.country}`}
                    </Typography>
                  )}
                </Box>

                {listing?.offer_type === 'ride_share' ? (
                  <Box>
                     <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        🚗 {t('listing.address.rideShareMapTitle', 'Carte du trajet')}
                     </Typography>
                     <RideShareMap 
                        startPosition={listing?.latitude && listing?.longitude ? [listing.latitude, listing.longitude] : null}
                        endPosition={listing?.booking_settings?.destinationLat && listing?.booking_settings?.destinationLng ? [listing.booking_settings.destinationLat, listing.booking_settings.destinationLng] : null}
                        readOnly={true}
                        height={350}
                     />
                     
                     <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Grid container spacing={2}>
                           <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                 {t('listing.map.departure', 'Départ')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                 {listing.address}
                              </Typography>
                           </Grid>
                           <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                 {t('listing.map.arrival', 'Arrivée')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                 {listing?.booking_settings?.destinationAddress || t('common.notSpecified')}
                              </Typography>
                           </Grid>
                        </Grid>
                     </Box>
                  </Box>
                ) : (
                  <ReadOnlyMap
                    latitude={listing?.latitude}
                    longitude={listing?.longitude}
                    address={listing?.address}
                    height={250}
                    zoom={16}
                    showAddress={false}
                  />
                )}

                {/* Note informative */}
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'block', 
                    mt: 1, 
                    fontStyle: 'italic',
                    textAlign: 'center'
                  }}
                >
                  ℹ️ {t('booking:listingView.locationPrivacy')}
                </Typography>
              </AccordionDetails>
            </Accordion>
            {/* Section Avis & Évaluations - Alignée sur la colonne principale */}
            <Accordion 
              defaultExpanded
              sx={{ 
                mb: 3,
                boxShadow: 3,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <StarIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {t('booking:listingView.sections.reviews', 'Avis des locataires')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <ListingReviewsList listingId={listing?.id} initialLimit={5} />
              </AccordionDetails>
            </Accordion>
          </Grid>
          
          {/* Section propriétaire - Colonne droite */}
          <Grid item xs={12} lg={3}>
            <Accordion 
              expanded={expandedPanels.owner}
              onChange={handlePanelChange('owner')}
              sx={{ 
                mb: 3,
                boxShadow: 3,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {t('booking:listingView.owner.title')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                {/* Informations principales du propriétaire */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
                    <Avatar 
                      src={listing?.owner?.profilePicture || listing?.owner?.profile_picture}
                      sx={{ 
                        width: 60, 
                        height: 60,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        mb: 1
                      }}
                    >
                      {listing?.owner?.name?.charAt(0) || 'U'}
                    </Avatar>
                    {ownerStats && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Rating 
                          value={ownerStats.averageRating || 0} 
                          precision={0.1} 
                          readOnly 
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {ownerStats.averageRating ? ownerStats.averageRating.toFixed(1) : '0.0'} ({ownerStats.totalReviews || 0})
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {listing?.owner?.name || t('booking:listingView.owner.anonymous')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {t('booking:listingView.owner.memberSince')} {formatDate(listing?.owner?.memberSince || listing?.owner?.createdAt || listing?.owner?.created_at)}
                    </Typography>
                    {listing?.owner?.verified && (
                      <Chip
                        icon={<VerifiedIcon />}
                        label={t('booking:listingView.owner.verified')}
                        size="small"
                        color="primary"
                        sx={{ mt: 1, fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Statistiques du propriétaire */}
                {ownerStatsLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {ownerStats?.totalListings || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <HomeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {t('booking:listingView.owner.stats.listings')}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          textAlign: 'center', 
                          bgcolor: 'grey.50',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'primary.light',
                            transform: 'translateY(-2px)',
                            boxShadow: 2
                          }
                        }}
                        onClick={() => setReputationModalOpen(true)}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <StarIcon sx={{ fontSize: 20, color: 'orange' }} />
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {t('booking:listingView.owner.viewReputationDetails')}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {ownerStats?.responseRate ? `${ownerStats.responseRate}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <ScheduleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {t('booking:listingView.owner.responsiveness')}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {/* Informations de contact */}
                {(listing?.owner?.email || listing?.owner?.phone) && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
                      {t('booking:listingView.owner.contact')}
                    </Typography>
                    <List dense>
                      {listing?.owner?.email && (
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <EmailIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={listing.owner.email}
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              sx: { wordBreak: 'break-word' }
                            }}
                          />
                        </ListItem>
                      )}
                      {listing?.owner?.phone && (
                        <ListItem sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <PhoneIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={listing.owner.phone}
                            primaryTypographyProps={{ 
                              variant: 'body2' 
                            }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}

                {/* Actions */}
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<EmailIcon />}
                    sx={{
                      background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                      }
                    }}
                  >
                    {t('booking:listingView.actions.contact')}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<TrendingUpIcon />}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'primary.light',
                        color: 'white'
                      }
                    }}
                  >
                    {t('booking:listingView.actions.viewProfile')}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>

        </Grid>
      </Container>

      {/* Composant ReservationSidebar unique (booking uniquement si listing actif) */}
      {isListingBookable && (
        <ReservationSidebar
          listing={listing}
          onClose={() => setSidebarOpen(false)}
          open={sidebarOpen}
        />
      )}

      {/* Modal de réputation du propriétaire */}
      {listing?.owner?.id && (
        <UserReputationModal
          open={reputationModalOpen}
          onClose={() => setReputationModalOpen(false)}
          userId={listing.owner.id}
        />
      )}
    </Box>
  );
};

export default ListingViewPage;
